import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { ThemeService } from '../../../services/theme.service';

export interface TabItem {
  id: string | number;
  label: string;
  icon?: string;
  iconColor?: string;
  hidden?: boolean;
  needsTranslation?: boolean;
}

@Component({
  selector: 'app-icam-tabs',
  templateUrl: './icam-tabs.component.html',
  styleUrl: './icam-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IcamTabsComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);

  @Input() tabs: TabItem[] = [];
  @Input() selectedTabId: string | number = 0;
  @Input() showArrow = true;
  @Input() arrowIconName = 'chevron-down';
  @Input() arrowIconHeight = '10px';
  @Input() minTabWidth = 'auto';
  @Input() spaced = true;

  isMobile = false;

  @Output() tabChange = new EventEmitter<TabItem>();

  isLight = true;

  ngOnInit() {
    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((isLight) => {
          this.isLight = isLight;
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    // compute mobile flag once on init so we avoid applying the inline min-width on small viewports
    try {
      this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 576;
    } catch {
      this.isMobile = false;
    }
    this._cdr.markForCheck();
  }

  onTabClick(tab: TabItem) {
    this.selectedTabId = tab.id;
    this.tabChange.emit(tab);
    this._cdr.markForCheck();
  }

  isTabSelected(tab: TabItem): boolean {
    return this.selectedTabId === tab.id;
  }

  getVisibleTabs(): TabItem[] {
    return this.tabs.filter((tab) => !tab.hidden);
  }

  /**
   * Return the min width to apply as an inline style. On small screens we avoid
   * setting the inline min-width so tabs can adapt to content + inline padding.
   */
  getMinTabWidth(): string | null {
    return this.isMobile ? null : this.minTabWidth;
  }

  getTabIcon(tab: TabItem): string {
    if (!tab.icon) return '';

    if (this.isTabSelected(tab)) {
      return `../assets/icon/light/${tab.icon}.svg`;
    } else if (!this.isLight) {
      return `../assets/icon/light/${tab.icon}.svg`;
    } else {
      return `../assets/icon/dark/${tab.icon}.svg`;
    }
  }
}
